import { useMemo } from 'react';
import { resolveEmbed } from '@/lib/embeds';

// ─────────────────────────────────────────────────────────────
// Aperçu intégré d'un lien (missions, preuves, partages).
// Utilise les URLs d'intégration officielles des réseaux sociaux
// (Facebook / Instagram / TikTok / YouTube) au lieu d'un iframe brut
// qui restait blanc. Quand un site refuse l'intégration, une carte
// d'aperçu claire remplace le cadre vide.
// ─────────────────────────────────────────────────────────────

interface Props {
  url: string;
  /** Titre accessible du cadre. */
  title?: string;
  className?: string;
}

export default function EmbedPreview({ url, title = 'Aperçu du contenu', className = '' }: Props) {
  const info = useMemo(() => resolveEmbed(url), [url]);

  if (info.kind === 'image') {
    return (
      <img
        src={url}
        alt={title}
        className={`w-full h-full object-contain bg-background-100 ${className}`}
      />
    );
  }

  if (info.kind === 'video') {
    return <video src={url} controls className={`w-full h-full bg-black ${className}`} />;
  }

  if (info.kind === 'audio') {
    return (
      <div className={`w-full h-full flex flex-col items-center justify-center gap-3 bg-background-100 px-6 ${className}`}>
        <i className="ri-mic-line text-2xl text-primary-500"></i>
        <audio src={url} controls className="w-full max-w-sm" />
      </div>
    );
  }

  if (info.kind === 'iframe' && info.src) {
    return (
      <iframe
        src={info.src}
        title={title}
        className={`w-full h-full bg-white ${className}`}
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-presentation"
        referrerPolicy="no-referrer"
        allow="clipboard-write; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
      ></iframe>
    );
  }

  // Pas d'intégration possible : carte d'aperçu, jamais un cadre vide.
  return (
    <div className={`w-full h-full flex flex-col items-center justify-center text-center px-6 bg-background-100 ${className}`}>
      <span className="w-12 h-12 rounded-full bg-background-50 text-primary-600 flex items-center justify-center mb-3">
        <i className={`${info.icon} text-xl`}></i>
      </span>
      <p className="text-xs font-bold text-foreground-900">{info.label}</p>
      <p className="mt-1 text-[11px] text-foreground-500 leading-relaxed max-w-[260px] break-all line-clamp-2">{url}</p>
      <p className="mt-2 text-[10px] text-foreground-400 leading-relaxed max-w-[260px]">{info.reason}</p>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-[11px] font-semibold whitespace-nowrap no-underline hover:bg-primary-600 transition-colors"
      >
        <i className="ri-external-link-line"></i>Ouvrir le lien
      </a>
    </div>
  );
}